import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';

const categoryIcons: Record<string, string> = {
  'category-shoes-001': 'ri-footprint-line',
  'category-hoodies-002': 'ri-shirt-line',
  'category-tshirts-003': 'ri-t-shirt-2-line',
  'category-jackets-004': 'ri-shirt-line',
  'category-pants-005': 'ri-pantone-line',
  'category-headwear-006': 'ri-user-smile-line',
  'category-sets-007': 'ri-stack-line',
  'category-underwear-008': 'ri-heart-pulse-line',
  'category-jersey-009': 'ri-medal-line',
  'category-accessories-010': 'ri-vip-diamond-line',
  'category-bags-011': 'ri-shopping-bag-3-line',
  'category-belts-012': 'ri-link',
  'category-perfumes-013': 'ri-drop-line',
  'category-other-014': 'ri-more-line',
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          scrolled || mobileMenuOpen
            ? 'bg-background-50 shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="w-full px-4 md:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <h1 className="font-heading text-xl md:text-2xl font-bold whitespace-nowrap">
              <Link
                to="/"
                onClick={closeMobileMenu}
                className={`${
                  scrolled || mobileMenuOpen ? 'text-foreground-900' : 'text-white'
                }`}
              >
                Superbuy Spreadsheet
              </Link>
            </h1>

            <div className="hidden md:flex items-center gap-6">
              <a
                href="#guides"
                className={`text-sm font-medium transition-colors whitespace-nowrap ${
                  scrolled
                    ? 'text-foreground-600 hover:text-foreground-900'
                    : 'text-white/80 hover:text-white'
                }`}
              >
                Guides
              </a>
              <a
                href="#safety"
                className={`text-sm font-medium transition-colors whitespace-nowrap ${
                  scrolled
                    ? 'text-foreground-600 hover:text-foreground-900'
                    : 'text-white/80 hover:text-white'
                }`}
              >
                Safety
              </a>
              <a
                href="#resources"
                className={`text-sm font-medium transition-colors whitespace-nowrap ${
                  scrolled
                    ? 'text-foreground-600 hover:text-foreground-900'
                    : 'text-white/80 hover:text-white'
                }`}
              >
                Resources
              </a>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden md:block relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                  scrolled
                    ? 'bg-background-100 text-foreground-800 hover:bg-background-200'
                    : 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm'
                }`}
              >
                Categories
                <i
                  className={`ri-arrow-down-s-line transition-transform duration-200 ${
                    dropdownOpen ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 right-0 w-64 bg-background-50 rounded-lg overflow-hidden shadow-lg border border-background-200/70 max-h-80 overflow-y-auto z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.seq}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-colors border-b border-background-100 last:border-b-0 whitespace-nowrap"
                    >
                      <span className="font-medium">{cat.displayName}</span>
                      <span className="block text-xs text-foreground-400 mt-0.5">
                        {cat.description}
                      </span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className={`hidden md:inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all ${
                scrolled
                  ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                  : 'bg-white text-foreground-900 hover:bg-white/90'
              }`}
            >
              SUPERBUY SHEET
              <i className="ri-arrow-right-up-line"></i>
            </a>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`md:hidden w-10 h-10 flex items-center justify-center rounded-full cursor-pointer transition-colors ${
                mobileMenuOpen
                  ? 'bg-foreground-100 text-foreground-900'
                  : scrolled
                    ? 'text-foreground-900 hover:bg-foreground-100'
                    : 'text-white hover:bg-white/10'
              }`}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              <i className={`text-xl transition-transform duration-300 ${mobileMenuOpen ? 'ri-close-line rotate-90' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        <div
          className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            mobileMenuOpen ? 'max-h-[85vh] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="bg-background-50 border-t border-background-200/70 px-4 py-5 overflow-y-auto max-h-[85vh]">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-100">
                <i className="ri-compass-3-line text-primary-600 text-xs"></i>
              </div>
              <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">Quick Links</span>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-5">
              <a
                href="#guides"
                onClick={closeMobileMenu}
                className="flex items-center gap-2.5 px-4 py-3 rounded-lg bg-background-100 text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer"
              >
                <div className="w-7 h-7 flex items-center justify-center rounded-full bg-accent-100 shrink-0">
                  <i className="ri-book-open-line text-accent-600 text-xs"></i>
                </div>
                <span className="text-xs font-semibold">Guides</span>
              </a>
              <a
                href="#safety"
                onClick={closeMobileMenu}
                className="flex items-center gap-2.5 px-4 py-3 rounded-lg bg-background-100 text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer"
              >
                <div className="w-7 h-7 flex items-center justify-center rounded-full bg-accent-100 shrink-0">
                  <i className="ri-shield-check-line text-accent-600 text-xs"></i>
                </div>
                <span className="text-xs font-semibold">Safety</span>
              </a>
              <a
                href="#resources"
                onClick={closeMobileMenu}
                className="flex items-center gap-2.5 px-4 py-3 rounded-lg bg-background-100 text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer"
              >
                <div className="w-7 h-7 flex items-center justify-center rounded-full bg-accent-100 shrink-0">
                  <i className="ri-database-2-line text-accent-600 text-xs"></i>
                </div>
                <span className="text-xs font-semibold">Resources</span>
              </a>
              <Link
                to="/superbuy-spreadsheet-beginner-guide"
                onClick={closeMobileMenu}
                className="flex items-center gap-2.5 px-4 py-3 rounded-lg bg-background-100 text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer"
              >
                <div className="w-7 h-7 flex items-center justify-center rounded-full bg-accent-100 shrink-0">
                  <i className="ri-graduation-cap-line text-accent-600 text-xs"></i>
                </div>
                <span className="text-xs font-semibold">Beginner Guide</span>
              </Link>
            </div>

            <div className="border-t border-background-200/70 pt-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-secondary-100">
                    <i className="ri-grid-line text-secondary-600 text-xs"></i>
                  </div>
                  <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                    Categories
                  </span>
                </div>
                <span className="text-[10px] font-medium text-foreground-300 bg-background-100 px-2 py-0.5 rounded-full">
                  14 items
                </span>
              </div>

              <div className="grid grid-cols-1 gap-1.5">
                {categories.map((cat) => {
                  const icon = categoryIcons[cat.seq] || 'ri-price-tag-3-line';
                  return (
                    <a
                      key={cat.seq}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={closeMobileMenu}
                      className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-background-100 active:bg-background-200/70 transition-colors cursor-pointer group"
                    >
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 group-hover:bg-accent-100 shrink-0 transition-colors">
                        <i className={`${icon} text-foreground-500 group-hover:text-accent-600 text-sm transition-colors`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-semibold text-foreground-800 block truncate">
                          {cat.displayName}
                        </span>
                        <span className="text-[10px] text-foreground-400 block truncate">
                          {cat.description}
                        </span>
                      </div>
                      <i className="ri-arrow-right-s-line text-foreground-300 group-hover:text-foreground-600 text-sm transition-colors shrink-0"></i>
                    </a>
                  );
                })}
              </div>
            </div>

            <div className="mt-5 pt-5 border-t border-background-200/70">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                onClick={closeMobileMenu}
                className="flex items-center justify-center gap-2 w-full px-6 py-3.5 rounded-full bg-primary-500 text-background-50 font-bold text-sm hover:bg-primary-600 transition-all cursor-pointer active:scale-[0.98]"
              >
                SUPERBUY SHEET
                <i className="ri-arrow-right-up-line"></i>
              </a>
              <p className="text-center text-[10px] text-foreground-300 mt-2.5">
                Visit kaspreadsheet.com for the full catalog
              </p>
            </div>
          </div>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-40 md:hidden"
          onClick={closeMobileMenu}
        ></div>
      )}
    </>
  );
}